/**
 * IndexedDB-backed cache for Nostr events
 * Provides instant retrieval of previously loaded events
 */

import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

const DB_NAME = 'nostr-cache';
const DB_VERSION = 1;
const EVENTS_STORE = 'events';
const METADATA_STORE = 'metadata';
const QUERIES_STORE = 'queries';

interface CachedEvent extends NostrEvent {
  _cached_at: number;
}

interface QueryCache {
  key: string;
  eventIds: string[];
  timestamp: number;
}

class NostrCacheDB {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<IDBDatabase> | null = null;

  async init(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Events store - indexed by event ID
        if (!db.objectStoreNames.contains(EVENTS_STORE)) {
          const eventsStore = db.createObjectStore(EVENTS_STORE, { keyPath: 'id' });
          eventsStore.createIndex('pubkey', 'pubkey', { unique: false });
          eventsStore.createIndex('kind', 'kind', { unique: false });
          eventsStore.createIndex('created_at', 'created_at', { unique: false });
          eventsStore.createIndex('_cached_at', '_cached_at', { unique: false });
        }

        // Metadata store - kind 0 events indexed by pubkey
        if (!db.objectStoreNames.contains(METADATA_STORE)) {
          const metadataStore = db.createObjectStore(METADATA_STORE, { keyPath: 'pubkey' });
          metadataStore.createIndex('_cached_at', '_cached_at', { unique: false });
        }

        // Query cache store - maps query keys to event IDs
        if (!db.objectStoreNames.contains(QUERIES_STORE)) {
          const queriesStore = db.createObjectStore(QUERIES_STORE, { keyPath: 'key' });
          queriesStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Store events in the cache
   */
  async putEvents(events: NostrEvent[]): Promise<void> {
    if (events.length === 0) return;

    const db = await this.init();
    const tx = db.transaction([EVENTS_STORE, METADATA_STORE], 'readwrite');
    const eventsStore = tx.objectStore(EVENTS_STORE);
    const metadataStore = tx.objectStore(METADATA_STORE);
    const now = Date.now();

    for (const event of events) {
      const cachedEvent: CachedEvent = { ...event, _cached_at: now };
      eventsStore.put(cachedEvent);

      // Also store kind 0 events in metadata store for quick author lookups
      if (event.kind === 0) {
        metadataStore.put({ pubkey: event.pubkey, event: cachedEvent, _cached_at: now });
      }
    }

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Get events by IDs
   */
  async getEventsByIds(ids: string[]): Promise<NostrEvent[]> {
    if (ids.length === 0) return [];

    const db = await this.init();
    const tx = db.transaction(EVENTS_STORE, 'readonly');
    const store = tx.objectStore(EVENTS_STORE);

    const events: NostrEvent[] = [];
    for (const id of ids) {
      const request = store.get(id);
      const event = await new Promise<CachedEvent | undefined>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      if (event) {
        // Remove the _cached_at field before returning
        const { _cached_at, ...nostrEvent } = event;
        events.push(nostrEvent as NostrEvent);
      }
    }

    return events;
  }

  /**
   * Get metadata (kind 0) events by pubkeys
   */
  async getMetadataByPubkeys(pubkeys: string[]): Promise<Map<string, NostrEvent>> {
    if (pubkeys.length === 0) return new Map();

    const db = await this.init();
    const tx = db.transaction(METADATA_STORE, 'readonly');
    const store = tx.objectStore(METADATA_STORE);

    const metadataMap = new Map<string, NostrEvent>();
    for (const pubkey of pubkeys) {
      const request = store.get(pubkey);
      const result = await new Promise<{ pubkey: string; event: CachedEvent; _cached_at: number } | undefined>(
        (resolve, reject) => {
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        }
      );
      if (result) {
        const { _cached_at, ...nostrEvent } = result.event;
        metadataMap.set(pubkey, nostrEvent as NostrEvent);
      }
    }

    return metadataMap;
  }

  /**
   * Query events by filter
   */
  async queryEvents(filter: NostrFilter, limit = 100): Promise<NostrEvent[]> {
    const db = await this.init();
    const tx = db.transaction(EVENTS_STORE, 'readonly');
    const store = tx.objectStore(EVENTS_STORE);

    let request: IDBRequest<CachedEvent[]>;

    // Use indexes when possible for better performance
    if (filter.kinds && filter.kinds.length === 1 && !filter.authors && !filter.ids) {
      const index = store.index('kind');
      request = index.getAll(filter.kinds[0], limit);
    } else if (filter.authors && filter.authors.length === 1 && !filter.kinds && !filter.ids) {
      const index = store.index('pubkey');
      request = index.getAll(filter.authors[0], limit);
    } else if (filter.ids) {
      // For IDs, use getEventsByIds which is more efficient
      return this.getEventsByIds(filter.ids);
    } else {
      // Fallback to full scan
      request = store.getAll();
    }

    const allEvents = await new Promise<CachedEvent[]>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    // Filter events in memory
    let filtered = allEvents.filter((event) => this.matchesFilter(event, filter));

    // Sort by created_at descending (newest first)
    filtered.sort((a, b) => b.created_at - a.created_at);

    // Apply limit
    if (filter.limit) {
      filtered = filtered.slice(0, filter.limit);
    } else if (limit) {
      filtered = filtered.slice(0, limit);
    }

    // Remove _cached_at field
    return filtered.map((event) => {
      const { _cached_at, ...nostrEvent } = event;
      return nostrEvent as NostrEvent;
    });
  }

  /**
   * Store query result for faster subsequent lookups
   */
  async cacheQuery(queryKey: string, eventIds: string[]): Promise<void> {
    const db = await this.init();
    const tx = db.transaction(QUERIES_STORE, 'readwrite');
    const store = tx.objectStore(QUERIES_STORE);

    const queryCache: QueryCache = {
      key: queryKey,
      eventIds,
      timestamp: Date.now(),
    };

    store.put(queryCache);

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Get cached query result
   */
  async getCachedQuery(queryKey: string, maxAge = 5 * 60 * 1000): Promise<NostrEvent[] | null> {
    const db = await this.init();
    const tx = db.transaction(QUERIES_STORE, 'readonly');
    const store = tx.objectStore(QUERIES_STORE);

    const request = store.get(queryKey);
    const result = await new Promise<QueryCache | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!result) return null;

    // Check if cache is still valid
    const age = Date.now() - result.timestamp;
    if (age > maxAge) return null;

    // Retrieve events
    return this.getEventsByIds(result.eventIds);
  }

  /**
   * Clear old cache entries
   */
  async clearOldEntries(maxAge = 7 * 24 * 60 * 60 * 1000): Promise<void> {
    const db = await this.init();
    const cutoff = Date.now() - maxAge;

    // Clear old events
    const eventsTx = db.transaction(EVENTS_STORE, 'readwrite');
    const eventsStore = eventsTx.objectStore(EVENTS_STORE);
    const eventsIndex = eventsStore.index('_cached_at');
    const eventsRange = IDBKeyRange.upperBound(cutoff);
    const eventsRequest = eventsIndex.openCursor(eventsRange);

    eventsRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    await new Promise<void>((resolve, reject) => {
      eventsTx.oncomplete = () => resolve();
      eventsTx.onerror = () => reject(eventsTx.error);
    });

    // Clear old metadata
    const metaTx = db.transaction(METADATA_STORE, 'readwrite');
    const metaStore = metaTx.objectStore(METADATA_STORE);
    const metaIndex = metaStore.index('_cached_at');
    const metaRange = IDBKeyRange.upperBound(cutoff);
    const metaRequest = metaIndex.openCursor(metaRange);

    metaRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    await new Promise<void>((resolve, reject) => {
      metaTx.oncomplete = () => resolve();
      metaTx.onerror = () => reject(metaTx.error);
    });

    // Clear old queries
    const queriesTx = db.transaction(QUERIES_STORE, 'readwrite');
    const queriesStore = queriesTx.objectStore(QUERIES_STORE);
    const queriesIndex = queriesStore.index('timestamp');
    const queriesRange = IDBKeyRange.upperBound(cutoff);
    const queriesRequest = queriesIndex.openCursor(queriesRange);

    queriesRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    await new Promise<void>((resolve, reject) => {
      queriesTx.oncomplete = () => resolve();
      queriesTx.onerror = () => reject(queriesTx.error);
    });
  }

  /**
   * Check if event matches filter
   */
  private matchesFilter(event: NostrEvent, filter: NostrFilter): boolean {
    if (filter.ids && !filter.ids.includes(event.id)) return false;
    if (filter.authors && !filter.authors.includes(event.pubkey)) return false;
    if (filter.kinds && !filter.kinds.includes(event.kind)) return false;

    if (filter.since && event.created_at < filter.since) return false;
    if (filter.until && event.created_at > filter.until) return false;

    // Check tag filters
    if (filter['#d']) {
      const dTags = event.tags.filter((tag) => tag[0] === 'd').map((tag) => tag[1]);
      if (!filter['#d'].some((d) => dTags.includes(d))) return false;
    }
    if (filter['#e']) {
      const eTags = event.tags.filter((tag) => tag[0] === 'e').map((tag) => tag[1]);
      if (!filter['#e'].some((e) => eTags.includes(e))) return false;
    }
    if (filter['#p']) {
      const pTags = event.tags.filter((tag) => tag[0] === 'p').map((tag) => tag[1]);
      if (!filter['#p'].some((p) => pTags.includes(p))) return false;
    }

    return true;
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{ events: number; metadata: number; queries: number }> {
    const db = await this.init();
    const tx = db.transaction([EVENTS_STORE, METADATA_STORE, QUERIES_STORE], 'readonly');

    const eventsCount = await new Promise<number>((resolve, reject) => {
      const request = tx.objectStore(EVENTS_STORE).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const metadataCount = await new Promise<number>((resolve, reject) => {
      const request = tx.objectStore(METADATA_STORE).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const queriesCount = await new Promise<number>((resolve, reject) => {
      const request = tx.objectStore(QUERIES_STORE).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    return { events: eventsCount, metadata: metadataCount, queries: queriesCount };
  }
}

// Singleton instance
const nostrCache = new NostrCacheDB();

/**
 * Generate a stable query key from filter
 */
export function generateQueryKey(filters: NostrFilter[]): string {
  return JSON.stringify(
    filters.map((f) => ({
      ...f,
      // Sort arrays for consistent keys
      ids: f.ids?.slice().sort(),
      authors: f.authors?.slice().sort(),
      kinds: f.kinds?.slice().sort(),
    }))
  );
}

export { nostrCache };
export type { CachedEvent, QueryCache };
